/**
 * FileActionsCard — the Actions card of the File Detail sidebar
 * (decomposition step F5a, CLAUDE.md §8.4).
 *
 * A pure JSX move: the card renders every status action for every role
 * exactly as it did inside `views/FileDetail.jsx`, and holds no state of
 * its own. Every handler it calls still lives in the view (or in the F3 /
 * F4 / F6 modules) and arrives as a prop under the identifier the JSX
 * already read, so the body below is byte-for-byte the former view block:
 *
 *   - `handleStatusChange`, `setShowPauseModal` — the status flow (the
 *     view's until step F5b moves it into `hooks/useFileStatusActions`);
 *   - `handover` — the F3 hook's return (`handover.start`);
 *   - `screenshots` — the F4 hook's return, passed whole;
 *   - `concurrency` — `useConcurrencyRules` (`concurrency.canMoveFile`);
 *   - `highlightUpload`, `setScreenshotWarning`, `setHighlightUpload` —
 *     the screenshot completion gate, whose banner and auto-dismiss effect
 *     stay in the view;
 *   - `handlePriorityChange`, `setShowUrgentConfirm`, `setShowRejectModal`,
 *     `purging`, `setShowPurgeConfirm`, `deleting`, `setShowDeleteConfirm` —
 *     the urgent, reject, purge and delete buttons; their handlers, modals
 *     and toasts stay in the view (purge and delete per F6).
 *
 * The role × status gating that decides which buttons render is the
 * view-side enforcement of the §7.6 pipeline rules and is reproduced
 * exactly; the table in the F5a PR body is its specification.
 */

import React from 'react';
import {
  ArrowLeft, Play, Pause, Send, CheckCircle2,
  Upload, FileText, Trash2, Zap,
} from 'lucide-react';
import CaseScreenshotsPanel from './caseScreenshots/CaseScreenshotsPanel';
import { sharedStyles } from './FileDetail.styles';

export default function FileActionsCard({
  file,
  role,
  allFiles,
  concurrency,
  actionLoading,
  handleStatusChange,
  setShowPauseModal,
  handover,
  screenshots,
  highlightUpload,
  setScreenshotWarning,
  setHighlightUpload,
  handlePriorityChange,
  setShowUrgentConfirm,
  purging,
  setShowPurgeConfirm,
  deleting,
  setShowDeleteConfirm,
  setShowRejectModal,
}) {
  return (
          <div className="card" style={styles.actionsCard}>
            <h3 style={styles.actionsTitle}>Actions</h3>
            <div style={styles.actionsList}>
              {/* Context-aware buttons based on current status */}
              {file.status === 'submitted' && role === 'assistant' && (() => {
                /* Queue restriction: assistants can only draw the top file in
                   the queue. Test records are excluded from the queue-top
                   computation AND bypass every draw restriction themselves
                   (CLAUDE.md §33) — real records keep all rules unchanged. */
                const topFileId = allFiles
                  .filter((f) => f.status === 'submitted' && !f.isTest)
                  .sort((a, b) => {
                    if (a.priority === 'Urgent' && b.priority !== 'Urgent') return -1;
                    if (a.priority !== 'Urgent' && b.priority === 'Urgent') return 1;
                    return new Date(a.submittedAt) - new Date(b.submittedAt);
                  })[0]?.id;
                const isTopFile = file.isTest || topFileId === file.id;
                // Per-record gate (canMoveFile) short-circuits for test
                // records at the hook level; the !file.isTest component
                // guard is kept as belt-and-braces (CLAUDE.md §33.4).
                const blockedByActiveFile = !file.isTest && !concurrency.canMoveFile(file);
                const isDisabled = !isTopFile || blockedByActiveFile;
                // Active-file rule takes precedence in the tooltip — it's the
                // more common blocker once the user is at the top of the queue.
                const tooltip = blockedByActiveFile
                  ? 'Only one file can be in progress at a time.'
                  : !isTopFile
                    ? 'Complete your current queue position first'
                    : undefined;
                return (
                  <>
                    <button
                      className="btn btn-primary"
                      style={{
                        ...styles.actionBtn,
                        ...(isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {})
                      }}
                      onClick={() => handleStatusChange('in_progress')}
                      disabled={actionLoading || isDisabled}
                      title={tooltip}
                    >
                      <Play size={16} /> Draw File
                    </button>
                    {blockedByActiveFile && (
                      <p style={{ color: 'var(--inbox-warning)', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Only one file can be in progress at a time.
                      </p>
                    )}
                    {!blockedByActiveFile && !isTopFile && (
                      <p style={{ color: 'var(--inbox-warning)', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        This file is not at the top of the queue. Draw the top file first.
                      </p>
                    )}
                  </>
                );
              })()}

              {role === 'assistant' && (
                <>
                  {file.status === 'in_progress' && (
                    <>
                      <button
                        className="btn btn-accent"
                        style={styles.actionBtn}
                        onClick={() => setShowPauseModal(true)}
                        disabled={actionLoading}
                      >
                        <Pause size={16} /> Pause File
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={styles.actionBtn}
                        onClick={() => handleStatusChange('sent_to_sign')}
                        disabled={actionLoading}
                      >
                        <Send size={16} /> Awaiting Signature
                      </button>
                    </>
                  )}

                  {file.status === 'paused' && (() => {
                    // Strict invariant: resume is allowed only when the
                    // In Progress slot is empty. If it's occupied, the
                    // user must pause / send-for-signature the active
                    // file first (CLAUDE.md §7.6). Test records bypass
                    // the slot rule entirely (CLAUDE.md §33) — enforced
                    // at the hook gate (canMoveFile) with the component
                    // guard kept as belt-and-braces.
                    const blockedByActiveFile = !file.isTest && !concurrency.canMoveFile(file);
                    const tooltip = blockedByActiveFile
                      ? 'Only one file can be in progress at a time.'
                      : undefined;
                    return (
                      <>
                        <button
                          className="btn btn-primary"
                          style={{
                            ...styles.actionBtn,
                            ...(blockedByActiveFile ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                          }}
                          onClick={() => handleStatusChange('in_progress')}
                          disabled={actionLoading || blockedByActiveFile}
                          title={tooltip}
                        >
                          <Play size={16} /> Resume File
                        </button>
                        {blockedByActiveFile && (
                          <p style={{ color: 'var(--inbox-warning)', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            Only one file can be in progress at a time.
                          </p>
                        )}
                      </>
                    );
                  })()}

                  {file.status === 'sent_to_sign' && (
                    <button
                      className="btn btn-secondary"
                      style={styles.actionBtn}
                      onClick={handover.start}
                      disabled={actionLoading}
                    >
                      <CheckCircle2 size={16} /> Signed & Sent to New Business
                    </button>
                  )}
                </>
              )}

              {/* Signed status actions — final completion step (both roles).
                  Two layouts here: legacy files (caseCount IS NULL) keep the
                  original single-screenshot upload + Mark as Completed
                  button; multi-case files swap in the per-case upload list
                  with a count-based gate on the move-to-completed button. */}
              {file.status === 'signed' && !screenshots.hasCases && (
                <>
                  <div style={{
                    ...styles.screenshotUpload,
                    ...(highlightUpload ? styles.screenshotUploadHighlight : {}),
                  }}>
                    <label style={styles.uploadLabel}>
                      <Upload size={14} />
                      {file.atworkScreenshotUrl ? 'Replace atwork Screenshot' : 'Upload atwork Screenshot'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={screenshots.handleScreenshotUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                  {file.atworkScreenshotUrl && (
                    <p style={styles.screenshotDone}>
                      <CheckCircle2 size={14} color="#2EC4B6" /> Screenshot uploaded
                    </p>
                  )}
                  <button
                    className="btn btn-secondary"
                    style={styles.actionBtn}
                    onClick={() => {
                      if (!file.atworkScreenshotUrl) {
                        setScreenshotWarning(
                          'Please upload the AtWork screenshot before marking this file as completed. This confirms that instructions have been submitted to the relevant companies.'
                        );
                        setHighlightUpload(true);
                        return;
                      }
                      handleStatusChange('completed');
                    }}
                    disabled={actionLoading}
                  >
                    <FileText size={16} /> Mark as Completed
                  </button>
                </>
              )}

              {file.status === 'signed' && screenshots.hasCases && (
                <CaseScreenshotsPanel
                  file={file}
                  caseLabels={file.caseLabels || []}
                  caseCount={file.caseCount}
                  screenshotsByIndex={screenshots.screenshotsByIndex}
                  uploadedCount={screenshots.uploadedCaseCount}
                  allUploaded={screenshots.allCaseScreenshotsUploaded}
                  onUpload={screenshots.handleCaseScreenshotUpload}
                  uploadingIndex={screenshots.caseUploadingIndex}
                  uploadStage={screenshots.caseUploadStage}
                  rowErrors={screenshots.caseRowErrors}
                  onDismissRowError={screenshots.dismissRowError}
                  onRequestOverride={screenshots.requestOverride}
                  onOpenFlagDetails={screenshots.openFlagDetails}
                  isAdviser={role === 'adviser'}
                  onOpenLightbox={screenshots.openLightbox}
                  onMoveToCompleted={() => {
                    if (!screenshots.allCaseScreenshotsUploaded) {
                      setScreenshotWarning(
                        `Upload all ${file.caseCount} AtWork screenshots before completing — ${screenshots.uploadedCaseCount} of ${file.caseCount} done.`
                      );
                      return;
                    }
                    handleStatusChange('completed');
                  }}
                  actionLoading={actionLoading}
                />
              )}

              {file.status === 'completed' && (
                <div style={styles.completedBox}>
                  <CheckCircle2 size={24} color="#2EC4B6" />
                  <p style={styles.completedText}>File completed</p>
                  {file.qualifiesForBonus && (
                    <span style={styles.bonusTag}>Bonus Qualifying ✓</span>
                  )}
                </div>
              )}

              {/* Mark as Urgent / Remove Urgent — adviser only, submitted files */}
              {role === 'adviser' && file.status === 'submitted' && (
                file.priority === 'Urgent' ? (
                  <button
                    style={styles.removeUrgentBtn}
                    onClick={() => handlePriorityChange('Normal')}
                    disabled={actionLoading}
                    type="button"
                  >
                    <Zap size={16} /> Remove Urgent
                  </button>
                ) : (
                  <button
                    style={styles.urgentBtn}
                    onClick={() => setShowUrgentConfirm(true)}
                    disabled={actionLoading}
                    type="button"
                  >
                    <Zap size={16} /> Mark as Urgent
                  </button>
                )
              )}

              {/* Purge attachments — adviser only, "escape hatch" (CLAUDE.md
                  §29). Shown at any stage while there are working copies still
                  in storage. Hidden once purged or when there are none. */}
              {role === 'adviser' &&
                file.attachments &&
                file.attachments.length > 0 &&
                !file.attachmentsPurgedAt && (
                  <button
                    style={styles.purgeBtn}
                    onClick={() => setShowPurgeConfirm(true)}
                    disabled={actionLoading || purging}
                    type="button"
                  >
                    <Trash2 size={16} /> Purge attachments now
                  </button>
                )}

              {/* Delete button — adviser only. On test records the action
                  is the explicit "Delete Test File" (CLAUDE.md §33) — this
                  labelled action never appears on real records; real
                  records keep the pre-existing generic delete unchanged. */}
              {role === 'adviser' && (
                <button
                  style={styles.deleteBtn}
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={actionLoading || deleting}
                  type="button"
                >
                  <Trash2 size={16} /> {file.isTest ? 'Delete Test File' : 'Delete File'}
                </button>
              )}

              {/* Reject & Move Back — adviser only, completed files */}
              {role === 'adviser' && file.status === 'completed' && (
                <button
                  style={styles.rejectBtn}
                  onClick={() => setShowRejectModal(true)}
                  disabled={actionLoading}
                  type="button"
                >
                  <ArrowLeft size={16} /> Reject &amp; Move Back
                </button>
              )}
            </div>
          </div>
  );
}

// Style keys read only by this card, verbatim from `views/FileDetail.jsx`.
// `actionsTitle` is shared with the view's Timeline card, so it lives in
// `FileDetail.styles.js` and is re-attached here as a reference.
export const styles = {
  actionsCard: {
    padding: '1.25rem',
  },
  actionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  actionBtn: {
    width: '100%',
    justifyContent: 'center',
  },
  screenshotUpload: {},
  screenshotUploadHighlight: {
    border: '2px solid var(--cp-warning)',
    borderRadius: '8px',
    padding: '0.5rem',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  uploadLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    padding: '0.65rem',
    background: 'var(--cp-bg)',
    borderRadius: '8px',
    border: '1.5px dashed var(--cp-border)',
    fontSize: '0.85rem',
    color: 'var(--cp-primary)',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  screenshotDone: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    fontSize: '0.8rem',
    color: 'var(--cp-success)',
    fontWeight: 500,
    marginBottom: '0.25rem',
  },
  completedBox: {
    textAlign: 'center',
    padding: '1rem',
  },
  completedText: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    marginTop: '0.5rem',
  },
  bonusTag: {
    display: 'inline-block',
    marginTop: '0.5rem',
    padding: '0.25rem 0.6rem',
    background: 'rgba(46, 196, 182, 0.12)',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--cp-success)',
  },
  removeUrgentBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    width: '100%',
    padding: '0.65rem 1rem',
    marginTop: '0.75rem',
    background: 'none',
    border: '1.5px solid var(--cp-text-muted)',
    color: 'var(--cp-text-muted)',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },
  urgentBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    width: '100%',
    padding: '0.65rem 1rem',
    marginTop: '0.75rem',
    background: 'none',
    border: '1.5px solid var(--cp-accent)',
    color: 'var(--cp-accent)',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },
  purgeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    width: '100%',
    padding: '0.65rem 1rem',
    marginTop: '0.5rem',
    background: 'none',
    border: '1px solid var(--cp-warning)',
    color: 'var(--cp-warning)',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },
  deleteBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    width: '100%',
    padding: '0.65rem 1rem',
    marginTop: '0.75rem',
    borderTop: '1px solid var(--cp-surface-alt)',
    paddingTop: '1rem',
    background: 'none',
    border: 'none',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: 'var(--cp-surface-alt)',
    color: 'var(--cp-danger)',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },
  rejectBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    width: '100%',
    padding: '0.65rem 1rem',
    marginTop: '0.5rem',
    background: 'none',
    color: 'var(--cp-warning)',
    border: '1px solid var(--cp-warning)',
    borderRadius: '8px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  actionsTitle: sharedStyles.actionsTitle,
};
